import { useCallback, useEffect, useRef, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { FileRejection, useDropzone } from "react-dropzone";
import moment from "moment";

/** COMPONENT */
import { Box, CircularProgress, MenuItem, Typography } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import ModalCustom from "../../../component/modalCustom/modalCustom";
import InputTextField from "../../../component/input/inputTextField";
import InputDatePicker from "../../../component/input/inputDatePicker";
import FilterSelect from "../../../component/select/filterSelect";
import Loading from "../../../component/loading/loading";
import { notiError, notiSuccess } from "../../../component/notification/notifications";
import Cloudupload from "../../../assets/icon/cloudupload";
import ReceiptTable from "../components/ReceiptTable";

/** API */
import DepositRefundApi, { DepositRefundRow } from "../../../api/deposit-refund";

/** UTILS */
import { colors } from "../../../constants/colors";
import {
  DEPOSIT_REFUND_DOC_REF_MAX_LENGTH,
  DEPOSIT_REFUND_REVIEW_ACCEPT,
  DEPOSIT_REFUND_REVIEW_MAX_FILES,
  DEPOSIT_REFUND_REVIEW_MAX_FILE_SIZE,
  DEPOSIT_REFUND_REVIEW_MAX_NOTE_LENGTH,
  DEPOSIT_REFUND_REVIEW_OPTIONS,
  DEPOSIT_REFUND_STATUS,
  depositRefundStatusColor,
  depositRefundStatusLabel,
} from "../../../constants/depositRefund";
import { getApiErrorStatus } from "../../../utils/api-error.utils";
import { formatFileSize } from "../../../utils/common";
import {
  bahtToView,
  deductToView,
  formatMoneyInput,
  getDepositRefundError,
  parseDeductInput,
  round2,
  sanitizeMoneyInput,
  toAmount,
} from "../depositRefund.utils";
import { EMPTY_REVIEW_FORM, ReviewForm, validateReviewForm } from "../depositRefund.validate";

/** STYLE */
import * as Style from "../depositRefund.style";

type Props = {
  row: DepositRefundRow;
  onClose: () => void;
  /** สำเร็จแล้ว: parent ปิด modal + reload ตาราง ในจังหวะเดียว */
  onSuccess: () => void;
};

export default function ModalDepositRefundReview({ row, onClose, onSuccess }: Props) {
  /**
   * ยอดตั้งต้นของ 2 บรรทัดที่หักได้ (move_out_penalty_rate / damage_insurance_limit)
   * มีแต่ในเส้น detail ตารางรายการไม่ส่งมา จึงต้องโหลดเองตอนเปิด modal
   */
  const [moveOutBase, setMoveOutBase] = useState<number>(0);
  const [damageBase, setDamageBase] = useState<number>(0);
  const [isLoadingBase, setIsLoadingBase] = useState<boolean>(true);
  /**
   * เลขที่คำสั่งเรียกคืนแผงค้าที่เป็นที่มาของยอดหักที่เติมให้ — null = ไม่ได้เติม
   * ใช้แค่โชว์ที่มา ไม่ได้ส่งกลับ backend
   */
  const [prefillSourceNo, setPrefillSourceNo] = useState<string | null>(null);
  const [form, setForm] = useState<ReviewForm>(EMPTY_REVIEW_FORM);
  /** ช่องที่ผู้ใช้แตะแล้ว — กัน helperText แดงทั้ง modal ตอนเพิ่งเปิด */
  const [touched, setTouched] = useState<Partial<Record<keyof ReviewForm, boolean>>>({});
  const [files, setFiles] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  /** กันกดปุ่มรัวก่อน isSubmitting จะ re-render ทัน */
  const isSubmittingRef = useRef<boolean>(false);

  useEffect(() => {
    /** กัน setState หลัง unmount ตอนผู้ใช้ปิด modal ก่อน response กลับมา */
    let isMounted = true;

    const loadBase = async () => {
      setIsLoadingBase(true);
      try {
        const [detailRes, defaultRes] = await Promise.all([
          DepositRefundApi.findById("ADMIN", row.id_deposit_refund),
          /**
           * ยอดหักตั้งต้นจากคำสั่งเรียกคืนแผงค้าของสัญญาใบเดียวกัน
           * เป็นของแถม — พลาดแล้วต้องไม่บล็อกฟอร์ม เจ้าหน้าที่ยังกรอกเองได้
           * จึงกลืน error ไว้ตรงนี้ ไม่ให้ไป reject ทั้ง Promise.all
           */
          DepositRefundApi.deductDefault(row.id_deposit_refund).catch(() => null),
        ]);
        if (!isMounted) return;

        // เพดานยังอ่านจาก detail เหมือนเดิม เพราะเป็นค่าที่ใช้ validate
        // ถ้าไปพึ่งเส้น prefill เส้นเดียว เส้นนั้นล่ม = เพดานเป็น 0 แล้วกรอกอะไรไม่ได้เลย
        setMoveOutBase(toAmount(detailRes.data?.data?.move_out_penalty_rate) || 0);
        setDamageBase(toAmount(detailRes.data?.data?.damage_insurance_limit) || 0);

        const prefill = defaultRes?.data?.data;
        // has_default เป็นตัวตัดสินเดียว ห้ามใช้ source !== null แทน
        // (มีเคสที่คำสั่งอนุมัติแล้วแต่หัก 0 บาท -> source ครบแต่ไม่มีอะไรให้เติม)
        if (prefill?.has_default) {
          // ฟอร์มเก็บเป็น string จัดคอมมาให้เหมือนตอนผู้ใช้พิมพ์แล้ว blur
          // และไม่ markTouched เพื่อไม่ให้ helperText แดงเด้งตั้งแต่เพิ่งเปิด modal
          setForm((prev) => ({
            ...prev,
            deduct_move_out: formatMoneyInput(String(prefill.deduct_move_out ?? "")),
            deduct_damage: formatMoneyInput(String(prefill.deduct_damage ?? "")),
          }));
          setPrefillSourceNo(prefill.source?.deposit_refund_no || null);
        }
      } catch (error) {
        if (!isMounted) return;
        notiError(getDepositRefundError(error, "ดึงยอดเงินมัดจำของคำร้องไม่สำเร็จ"));
      } finally {
        if (isMounted) setIsLoadingBase(false);
      }
    };

    loadBase();
    return () => {
      isMounted = false;
    };
  }, [row.id_deposit_refund]);

  const errors = validateReviewForm(form, { moveOutBase, damageBase });
  const isApprove = form.result === DEPOSIT_REFUND_STATUS.APPROVE;
  const isNotApprove = form.result === DEPOSIT_REFUND_STATUS.NOT_APPROVE;

  /** โชว์ error เฉพาะช่องที่แตะแล้ว หรือหลังกดยืนยันไปแล้วรอบหนึ่ง */
  const errorOf = (key: keyof ReviewForm) => (touched[key] ? errors[key] : undefined);
  const markTouched = (key: keyof ReviewForm) =>
    setTouched((prev) => ({ ...prev, [key]: true }));

  const setField = (key: keyof ReviewForm, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    markTouched(key);
  };

  /**
   * ยอดที่จะได้คืนจริง — คำนวณแบบเดียวกับ server (clamp ยอดหักไม่ให้เกินยอดตั้งต้น)
   * โชว์สดๆ ให้เจ้าหน้าที่เห็นก่อนกดยืนยัน ไม่ต้องเดาว่าหักแล้วเหลือเท่าไหร่
   */
  const moveOutDeduct = Math.min(round2(parseDeductInput(form.deduct_move_out)), moveOutBase);
  const damageDeduct = Math.min(round2(parseDeductInput(form.deduct_damage)), damageBase);
  const approvedPreview = round2(
    round2(moveOutBase - moveOutDeduct) + round2(damageBase - damageDeduct)
  );

  /**
   * เปลี่ยนผลการพิจารณา — ไม่ล้างช่องยอดหักทิ้ง
   *
   * ค่าที่กรอก/ที่ระบบเติมให้จะไม่หลุดไปกับ payload อยู่แล้ว เพราะ:
   *  - body.append("deduct_*") อยู่ใน if (isApprove)
   *  - การ์ดคำนวณถูกซ่อนทั้งบล็อกเมื่อไม่อนุมัติ
   *  - validateReviewForm ตรวจ 2 ช่องนี้เฉพาะตอน APPROVE
   * ถ้าล้างทิ้ง เจ้าหน้าที่ที่สลับไปไม่อนุมัติแล้วสลับกลับจะเสียยอดที่ prefill มาให้
   */
  const handleChangeResult = (value: string) => {
    setForm((prev) => ({ ...prev, result: value }));
    markTouched("result");
  };

  /**
   * ออกจากช่องเงินแล้วจัดคอมมาให้ — อ่านค่าจาก updater ไม่ใช่ form ในสโคปนี้
   * เพราะถ้าผู้ใช้พิมพ์แล้ว blur ทันที ค่าใน closure อาจเป็นรอบ render ก่อนหน้า
   */
  const handleBlurMoney = (key: "deduct_move_out" | "deduct_damage") => {
    setForm((prev) => ({ ...prev, [key]: formatMoneyInput(prev[key]) }));
    markTouched(key);
  };

  const handleChangeRefundDate = (value: any) => {
    // InputDatePicker โชว์เป็น พ.ศ. แต่ค่าที่ส่ง backend ต้องเป็น ค.ศ. ISO
    setField("refund_date", value ? moment(value).format("YYYY-MM-DD") : "");
  };

  const onDropRejected = useCallback((rejections: FileRejection[]) => {
    const isTooBig = rejections.some((rejection) =>
      rejection.errors.some((error) => error.code === "file-too-large")
    );
    notiError(
      isTooBig
        ? `ไฟล์มีขนาดเกิน ${formatFileSize(DEPOSIT_REFUND_REVIEW_MAX_FILE_SIZE)}`
        : "รับเฉพาะไฟล์ PDF, JPG หรือ PNG"
    );
  }, []);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (!acceptedFiles?.length) return;

      const nextFiles = [...files];
      let duplicated = 0;
      let overflow = 0;

      acceptedFiles.forEach((acceptedFile) => {
        if (nextFiles.length >= DEPOSIT_REFUND_REVIEW_MAX_FILES) {
          overflow += 1;
          return;
        }
        // เทียบ name + size + lastModified เพราะ path ไม่ได้มีทุก browser
        const isDuplicated = nextFiles.some(
          (x) =>
            x.name === acceptedFile.name &&
            x.size === acceptedFile.size &&
            x.lastModified === acceptedFile.lastModified
        );
        if (isDuplicated) {
          duplicated += 1;
          return;
        }
        nextFiles.push(acceptedFile);
      });

      if (duplicated) notiError("ไฟล์นี้ถูกแนบไว้แล้ว");
      if (overflow) notiError(`แนบไฟล์ได้ไม่เกิน ${DEPOSIT_REFUND_REVIEW_MAX_FILES} ไฟล์`);
      setFiles(nextFiles);
    },
    [files]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDropRejected,
    accept: DEPOSIT_REFUND_REVIEW_ACCEPT,
    multiple: true,
    maxSize: DEPOSIT_REFUND_REVIEW_MAX_FILE_SIZE,
    disabled: isSubmitting,
  });

  /** ลบด้วย index ไม่ใช่ path — path เป็น undefined ได้ทุกไฟล์ ซึ่งจะทำให้ filter ลบยกลิสต์ */
  const handleRemoveFile = (index: number) =>
    setFiles((prev) => prev.filter((_, i) => i !== index));

  const onSubmit = async () => {
    // กดยืนยันแล้วให้เห็น error ทุกช่องที่ยังไม่ผ่าน ไม่ใช่เฉพาะช่องที่แตะ
    setTouched({
      result: true,
      refund_date: true,
      doc_ref: true,
      note: true,
      deduct_move_out: true,
      deduct_damage: true,
    });
    if (Object.keys(errors).length > 0 || isSubmittingRef.current) return;

    isSubmittingRef.current = true;
    setIsSubmitting(true);
    try {
      // endpoint นี้รับ multipart ตรงๆ ไม่ใช่ 2-step แบบ upload อื่นในระบบ
      // ไม่ set Content-Type เอง ต้องให้ browser ใส่ boundary ของ multipart
      const body = new FormData();
      body.append("result", form.result);
      if (form.note.trim()) body.append("note", form.note.trim());
      if (isApprove) {
        // ไม่อนุมัติ = ไม่มีการคืนเงิน จึงไม่ส่งยอดหัก/วันที่/เลขเอกสารไปเลย
        body.append("deduct_move_out", String(parseDeductInput(form.deduct_move_out)));
        body.append("deduct_damage", String(parseDeductInput(form.deduct_damage)));
        if (form.refund_date) body.append("refund_date", form.refund_date);
        if (form.doc_ref.trim()) body.append("doc_ref", form.doc_ref.trim());
      }
      files.forEach((file) => body.append("files", file));

      await DepositRefundApi.review(row.id_deposit_refund, body);
      notiSuccess("บันทึกผลการพิจารณาสำเร็จ");
      onSuccess();
    } catch (error) {
      const status = getApiErrorStatus(error);
      const message = getDepositRefundError(error, "บันทึกผลการพิจารณาไม่สำเร็จ");
      notiError(message);
      // 404 / พิจารณาไปแล้ว / ยกเลิกไปแล้ว = ข้อมูลบนหน้าจอไม่ตรงกับ DB แล้ว ปิดแล้วโหลดใหม่
      if (status === 404 || message.includes("พิจารณาไปแล้ว") || message.includes("ยกเลิกไปแล้ว")) {
        onSuccess();
      }
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const subTitle = [
    row.deposit_refund_no,
    row.tenant_name,
    bahtToView(row.request_amount),
  ]
    .filter(Boolean)
    .join(" · ");

  /**
   * 1 แถวในตารางคำนวณเงินคืน — ยอดตั้งต้นเป็นช่องปิด (พื้นเทา) ให้เห็นว่าแก้ไม่ได้
   * ยอดที่หักเป็นช่องกรอก คงเหลือคำนวณสดให้เห็นผลทันทีที่พิมพ์
   */
  const renderDeductRow = (
    key: "deduct_move_out" | "deduct_damage",
    label: string,
    base: number,
    deducted: number
  ) => (
    <Style.DeductFormRow key={key}>
      <span>{label}</span>
      <InputTextField disabled value={deductToView(base)} inputTextAlign="right" />
      <InputTextField
        value={form[key]}
        disabled={isSubmitting}
        placeholder="0"
        inputTextAlign="right"
        helperText={errorOf(key)}
        // คัดตัวอักษรที่ไม่ใช่จำนวนเงินออกตอนพิมพ์ แล้วจัดคอมมาให้ตอนออกจากช่อง
        onchange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setField(key, sanitizeMoneyInput(e.target.value))
        }
        onblur={() => handleBlurMoney(key)}
      />
      <span>{deductToView(round2(base - deducted))}</span>
    </Style.DeductFormRow>
  );

  const renderSelectedFiles = () => (
    <Box sx={{ mt: 1 }}>
      <Typography sx={{ fontSize: 14, color: colors.textLightGray, mb: 1 }}>
        {`${files.length}/${DEPOSIT_REFUND_REVIEW_MAX_FILES} ไฟล์`}
      </Typography>
      {files.map((file, index) => (
        <Style.FileBox key={`${file.name}-${file.lastModified}-${index}`} sx={{ mt: 1 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
            <Typography sx={{ fontSize: 16, wordBreak: "break-all" }}>{file.name}</Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography sx={{ fontSize: 14, color: colors.textLightGray, whiteSpace: "nowrap" }}>
                {formatFileSize(file.size)}
              </Typography>
              <DeleteIcon
                color="error"
                sx={{ cursor: isSubmitting ? "not-allowed" : "pointer" }}
                onClick={() => {
                  if (!isSubmitting) handleRemoveFile(index);
                }}
              />
            </Box>
          </Box>
        </Style.FileBox>
      ))}
    </Box>
  );

  const renderBody = () => {
    if (isLoadingBase) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" sx={{ py: 6 }}>
          <CircularProgress size={32} sx={{ color: colors.themeMainColor }} />
        </Box>
      );
    }

    return (
    <Box
      className="custom-scroll"
      sx={{ px: { xs: 2, md: 3 }, pb: 1, maxHeight: "calc(100vh - 260px)", overflowY: "auto" }}
    >
      {subTitle && (
        <Typography sx={{ fontSize: 16, color: colors.textLightGray, textAlign: "center", mb: 3 }}>
          {subTitle}
        </Typography>
      )}

      <Box sx={{ mb: 3 }}>
        <FilterSelect
          labelId="deposit-refund-review-result"
          selectId="deposit-refund-review-result"
          heading="ผลการพิจารณา"
          required
          value={form.result}
          disabled={isSubmitting}
          // ต้องส่ง displayEmpty ไม่งั้น MUI ไม่เรียก renderValue ตอน value ยังว่าง
          // แล้วช่องจะว่างเปล่าไม่มี placeholder สีเทาตามดีไซน์
          displayEmpty
          helperText={errorOf("result")}
          onchange={(event: any) => handleChangeResult(event.target.value)}
          renderValue={() =>
            form.result ? (
              depositRefundStatusLabel(form.result)
            ) : (
              <span style={{ color: colors.black_60 }}>เลือกผลการพิจารณา</span>
            )
          }
          options={DEPOSIT_REFUND_REVIEW_OPTIONS.map((option: string) => (
            <MenuItem key={option} value={option}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    backgroundColor: depositRefundStatusColor(option),
                  }}
                />
                {depositRefundStatusLabel(option)}
              </Box>
            </MenuItem>
          ))}
        />
      </Box>

      {/*
        ตารางคำนวณเงินคืน — แสดงเฉพาะตอน "อนุมัติ" ตามดีไซน์
        ไม่อนุมัติ = ไม่มีการคืนเงิน จึงซ่อนทั้งบล็อก ไม่ใช่ปิดช่องไว้
      */}
      {isApprove && (
        <Box
          sx={{
            mb: 3,
            border: `1px solid ${colors.lightGray}`,
            borderRadius: "8px",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1.5,
              borderBottom: `1px solid ${colors.lightGray}`,
            }}
          >
            <Typography sx={{ fontSize: 16, fontWeight: 600 }}>
              รายละเอียดการคำนวณเงินคืน
            </Typography>
            {/* บอกที่มาเมื่อระบบเติมยอดให้ เจ้าหน้าที่จะได้รู้ว่าตัวเลขมาจากไหนและแก้ได้ */}
            {prefillSourceNo && (
              <Typography sx={{ fontSize: 14, color: colors.textLightGray, mt: 0.5 }}>
                {`ยอดหักตั้งต้นจากคำสั่งเรียกคืนแผงค้าเลขที่ ${prefillSourceNo} — แก้ไขได้`}
              </Typography>
            )}
          </Box>

          <Style.DeductRow
            sx={{
              backgroundColor: colors.extraLightGray,
              fontSize: 14,
              color: colors.textLightGray,
              py: 1,
            }}
          >
            <span>รายการ</span>
            <span>ยอดตั้งต้น</span>
            <span>ยอดที่หัก</span>
            <span>คงเหลือ</span>
          </Style.DeductRow>

          {renderDeductRow(
            "deduct_move_out",
            "ค่าปรับกรณีไม่ยอมออกจากพื้นที่",
            moveOutBase,
            moveOutDeduct
          )}
          {renderDeductRow(
            "deduct_damage",
            "วงเงินประกันความเสียหาย",
            damageBase,
            damageDeduct
          )}

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "baseline",
              gap: 2,
              px: 2,
              py: 1.5,
              borderTop: `1px solid ${colors.lightGray}`,
              backgroundColor: colors.extraLightGray,
            }}
          >
            <Typography sx={{ fontSize: 16, color: colors.textLightGray }}>
              ยอดเงินที่ได้รับการพิจารณา
            </Typography>
            <Typography sx={{ fontSize: 18, fontWeight: 600, color: colors.themeMainColor }}>
              {bahtToView(approvedPreview)}
            </Typography>
          </Box>
        </Box>
      )}

      {/*
        ตารางใบเสร็จของผู้เช่า/แผงในคำร้องนี้ — โชว์เฉพาะเคสอนุมัติตามดีไซน์
        ให้เจ้าหน้าที่ตรวจใบเสร็จก่อนกดยืนยันได้ ไม่ต้องออกไปหน้าใบแจ้งหนี้แล้วกลับมากรอกใหม่
      */}
      {isApprove && (
        <Box sx={{ mb: 3 }}>
          <ReceiptTable userId={row.userId} stallId={row.stallId} />
        </Box>
      )}

      <Row className="mb-3">
        <Col md={6} xs={12} className="my-1">
          <InputDatePicker
            heading="วันที่คืนเงิน"
            required={isApprove}
            value={form.refund_date || null}
            onChange={handleChangeRefundDate}
            onClear={() => handleChangeRefundDate(null)}
            allowClear
            placeholder="เลือกวันที่"
            disabled={isSubmitting}
            helperText={errorOf("refund_date")}
          />
        </Col>
        <Col md={6} xs={12} className="my-1">
          <InputTextField
            heading="เลขที่เอกสารอ้างอิง"
            required={isApprove}
            value={form.doc_ref}
            disabled={isSubmitting}
            placeholder="เช่น ฎกทท.1234/2569"
            inputProps={{ maxLength: DEPOSIT_REFUND_DOC_REF_MAX_LENGTH }}
            helperText={errorOf("doc_ref")}
            onchange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setField("doc_ref", e.target.value)
            }
          />
        </Col>
      </Row>

      <Box sx={{ mb: 3 }}>
        <InputTextField
          heading="รายละเอียดการพิจารณา"
          // ไม่อนุมัติต้องมีเหตุผลตามกฎหมายวิธีปฏิบัติราชการทางปกครอง
          required={isNotApprove}
          value={form.note}
          multiline
          rows={4}
          maxRows={4}
          // multiline ต้องคู่กับ inputHeight 'auto' ไม่งั้นกล่องถูกล็อกความสูง 43px
          inputHeight={"auto"}
          disabled={isSubmitting}
          placeholder="ระบุเหตุผลหรือรายละเอียดเพิ่มเติม"
          inputProps={{ maxLength: DEPOSIT_REFUND_REVIEW_MAX_NOTE_LENGTH }}
          helperText={errorOf("note")}
          onchange={(e: React.ChangeEvent<HTMLInputElement>) => setField("note", e.target.value)}
        />
      </Box>

      <Typography sx={{ fontSize: 16, mb: 1 }}>แนบไฟล์เอกสาร</Typography>
      <Box
        {...getRootProps()}
        sx={{
          width: "100%",
          border: `2px dashed ${colors.bgText}`,
          borderRadius: "8px",
          py: 3,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          cursor: isSubmitting ? "not-allowed" : "pointer",
          backgroundColor: isDragActive ? colors.extraLightGray : colors.white,
        }}
      >
        <input {...getInputProps()} />
        <Cloudupload />
        <Typography sx={{ mt: 1, fontSize: 16 }}>เลือกไฟล์ หรือลากไฟล์มาวาง</Typography>
        <Typography sx={{ fontSize: 14, color: colors.textLightGray }}>
          {`PDF, JPG, PNG ขนาดไม่เกิน ${formatFileSize(DEPOSIT_REFUND_REVIEW_MAX_FILE_SIZE)}`}
        </Typography>
      </Box>
      {files.length > 0 && renderSelectedFiles()}

      {/* พิจารณาแล้วแก้ไม่ได้ ไม่มี endpoint แก้ผลการพิจารณา (แจ้ง BE ไว้แล้ว) */}
      <Typography sx={{ fontSize: 14, color: colors.red, mt: 2 }}>
        เมื่อบันทึกผลการพิจารณาแล้วจะแก้ไขไม่ได้ กรุณาตรวจสอบยอดเงินให้ถูกต้องก่อนยืนยัน
      </Typography>
    </Box>
    );
  };

  return (
    <>
      {isSubmitting && <Loading show />}
      <ModalCustom
        // ต้องเป็น true เสมอ + ให้ parent conditional render (ค่า false จะ fall back ไปอ่าน redux)
        isShowProps
        // ปิด modal เองผ่าน onClose ไม่ให้ไป reset redux modal.isShow ของ modal อื่นในหน้าเดียวกัน
        autoResetModal={false}
        // popper ของ InputDatePicker render ผ่าน portal ออกนอก modal
        // ถ้า enforceFocus react-bootstrap จะแย่ง focus กลับ ปฏิทินเลือกวันไม่ได้
        enforceFocus={false}
        size="lg"
        closeButton
        alignFooter="end"
        footerInline
        title="พิจารณาคำร้องคืนค่ามัดจำ"
        component={renderBody()}
        textBtnCancel="ยกเลิก"
        onReset={onClose}
        textBtnConfirm="ยืนยัน"
        onSubmit={onSubmit}
        disabledSubmit={Object.keys(errors).length > 0 || isSubmitting || isLoadingBase}
        onClose={onClose}
      />
    </>
  );
}
